/**
 * JobsFeed — teacher-facing list of public jobs across institutions.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import {
  Briefcase, MapPin, Banknote, Calendar, Search, Filter, Loader2,
  Bookmark, Check, Sparkles, ArrowRight, FileText, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import CoverLetterDialog from "@/components/CoverLetterDialog";
import ShareButton from "@/components/ShareButton";

export default function JobsFeed() {
  const navigate = useNavigate();
  const [facets, setFacets] = useState(null);
  const [items, setItems] = useState(null);
  const [recommended, setRecommended] = useState(null);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ q: "", category: "", mode: "", is_extra_curricular: null });
  const [loading, setLoading] = useState(false);
  const [coverFor, setCoverFor] = useState(null);

  const run = async (override = {}, isInitial = false) => {
    setLoading(true);
    try {
      const params = { ...override };
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
      const [feed, saved] = await Promise.all([
        api.get("/marketplace/jobs/feed", { params }),
        api.get("/marketplace/jobs/saved").catch(() => ({ data: [] })),
      ]);
      const savedSet = new Set((saved.data || []).map((j) => j.job_id));
      const merged = (feed.data || []).map((j) => ({ ...j, saved: j.saved ?? savedSet.has(j.job_id) }));
      setItems(merged);
      setTotal(merged.length);
    } catch (err) {
      logQuietError("jobs.feed", err);
      if (!isInitial) toast.error("Could not load jobs");
    } finally {
      setLoading(false);
    }
  };

  // Initial load only — explicit deps would re-fire on every keystroke. Filters
  // are applied via the explicit "Search" button (run() with no override).
  useEffect(() => {
    api.get("/marketplace/facets").then((r) => setFacets(r.data)).catch((e) => logQuietError("jobs.facets", e));
    api.get("/marketplace/jobs/recommended")
      .then((r) => setRecommended(r.data))
      .catch(() => setRecommended([]));
    run({}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSave = async (job_id) => {
    try {
      const { data } = await api.post(`/marketplace/jobs/${job_id}/save`);
      setItems((curr) => curr.map((j) => j.job_id === job_id ? { ...j, saved: data.saved } : j));
      toast.success(data.saved ? "Saved to your list" : "Removed from saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  const apply = async (job_id) => {
    try {
      await api.post(`/marketplace/jobs/${job_id}/apply`, { cover_letter: "" });
      setItems((curr) => curr.map((j) => j.job_id === job_id ? { ...j, applied: true } : j));
      toast.success("Application sent", { description: "Recruiters will see your TeeBee profile." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply");
    }
  };

  return (
    <Layout title="Jobs for Educators">
      <div className="mb-5">
        <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Briefcase className="h-3 w-3" /> TeeBee Educator Marketplace
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">Jobs for educators</h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
          Curated roles across curricular and extra-curricular categories. Apply with one click using your TeeBee profile.
        </p>
      </div>

      {recommended && recommended.length > 0 && (
        <section className="mb-5" data-testid="jobs-recommended">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2 mb-3">
            <Sparkles className="h-3 w-3" /> Recommended for you
          </p>
          <div className="flex gap-3 overflow-x-auto tb-no-scrollbar pb-1 -mx-1 px-1 tb-stagger">
            {recommended.slice(0, 6).map((j) => (
              <div key={j.job_id} className="tb-card-interactive p-3 min-w-[260px] max-w-[280px] shrink-0" data-testid={`reco-${j.job_id}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary inline-flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> {j.match_score}% match
                  </span>
                  {j.applied && <span className="tb-chip-mint rounded-full text-[10px] px-1.5 py-0.5">Applied</span>}
                </div>
                <p className="font-display text-sm font-medium tracking-tight line-clamp-1">{j.role_title}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{j.org_name}</p>
                {j.match_reasons?.[0] && (
                  <p className="text-[11px] mt-2 text-muted-foreground line-clamp-2 inline-flex items-start gap-1">
                    <Check className="h-3 w-3 mt-0.5 text-emerald-500 shrink-0" /> {j.match_reasons[0]}
                  </p>
                )}
                <div className="mt-3 flex gap-1.5">
                  <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => apply(j.job_id)} disabled={j.applied} data-testid={`reco-apply-${j.job_id}`}>
                    {j.applied ? <Check className="h-3 w-3" /> : "Apply"}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setCoverFor(j)} data-testid={`reco-cover-${j.job_id}`}>
                    <FileText className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="tb-card p-4 mb-5 grid grid-cols-1 md:grid-cols-10 gap-2.5" data-testid="jobs-filters">
        <div className="md:col-span-4 relative">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="tb-input pl-9" placeholder="Search role, org, description..."
                 value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                 onKeyDown={(e) => e.key === "Enter" && run()} data-testid="jobs-q" />
        </div>
        <select className="tb-input md:col-span-2" value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })} data-testid="jobs-cat">
          <option value="">All categories</option>
          {facets && [...facets.curricular_categories, "—", ...facets.extra_curricular_categories].map((c) =>
            c === "—" ? <option key="sep" disabled>──────────</option> : <option key={c} value={c}>{c}</option>
          )}
        </select>
        <select className="tb-input md:col-span-2" value={filters.is_extra_curricular === null ? "" : String(filters.is_extra_curricular)}
                onChange={(e) => setFilters({ ...filters, is_extra_curricular: e.target.value === "" ? null : e.target.value === "true" })} data-testid="jobs-type">
          <option value="">All types</option>
          <option value="false">Curricular</option>
          <option value="true">Extra-curricular</option>
        </select>
        <select className="tb-input md:col-span-1" value={filters.mode}
                onChange={(e) => setFilters({ ...filters, mode: e.target.value })} data-testid="jobs-mode">
          <option value="">Mode</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="hybrid">Hybrid</option>
        </select>
        <Button onClick={() => run()} disabled={loading} className="rounded-full md:col-span-1" data-testid="jobs-search">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Filter className="h-3.5 w-3.5" />}
        </Button>
      </section>

      {!items ? (
        <div className="space-y-2"><Skeleton className="h-28" /><Skeleton className="h-28" /></div>
      ) : items.length === 0 ? (
        <div className="tb-card p-10 text-center" data-testid="jobs-empty">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <Briefcase className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">No jobs yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Be the first when a recruiter posts. We'll surface roles that match your profile.</p>
        </div>
      ) : (
        <div className="space-y-3 tb-stagger">
          {items.map((j) => (
            <div key={j.job_id} className="tb-card p-4 flex items-start gap-3" data-testid={`job-card-${j.job_id}`}>
              <span className="h-10 w-10 rounded-2xl tb-chip-lavender grid place-items-center font-display text-sm">{(j.org_name || "?").slice(0, 1)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-display text-base font-medium tracking-tight">{j.role_title}</p>
                  {j.boosted && <span className="tb-chip-butter rounded-full text-[10px] px-2 py-0.5 font-semibold inline-flex items-center gap-1"><Sparkles className="h-2.5 w-2.5" /> Featured</span>}
                </div>
                <p className="text-[12px] text-muted-foreground">{j.org_name} · {j.category || "—"} · {(j.job_type || "").replace(/_/g, " ")}</p>
                <p className="text-[13px] mt-1 line-clamp-2">{j.description}</p>
                <ul className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                  {j.location && <li className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {j.location}</li>}
                  {j.salary_min && j.salary_max && <li className="inline-flex items-center gap-1"><Banknote className="h-3 w-3" /> ₹{j.salary_min.toLocaleString()}–₹{j.salary_max.toLocaleString()}</li>}
                  {j.deadline && <li className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Due {j.deadline.slice(0, 10)}</li>}
                </ul>
              </div>
              <div className="flex flex-col gap-1.5 ml-2">
                <Button size="sm" variant={j.saved ? "outline" : "ghost"} className="rounded-full" onClick={() => toggleSave(j.job_id)} data-testid={`job-save-${j.job_id}`}>
                  <Bookmark className={`h-3 w-3 mr-1 ${j.saved ? "fill-current" : ""}`} /> {j.saved ? "Saved" : "Save"}
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setCoverFor(j)} data-testid={`job-cover-${j.job_id}`}>
                  <FileText className="h-3 w-3 mr-1" /> Cover letter
                </Button>
                <Button size="sm" className="rounded-full" onClick={() => apply(j.job_id)} disabled={j.applied} data-testid={`job-apply-${j.job_id}`}>
                  {j.applied ? <Check className="h-3 w-3 mr-1" /> : <ArrowRight className="h-3 w-3 mr-1" />}
                  {j.applied ? "Applied" : "Apply"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CoverLetterDialog open={!!coverFor} job={coverFor} onClose={() => setCoverFor(null)} />
    </Layout>
  );
}
